// Eight digits in the CEP, and the street, the neighborhood and the city fill in
// (walk/ADDRESS.md, docs/ADDRESS.md § CEP → the rest). Number and complement
// are never touched, a street somebody already typed is never overwritten,
// and a CEP nobody knows changes nothing but a quiet hint.
import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['nation', 'zip', 'street', 'number', 'hood', 'cityId', 'city', 'missing', 'search'];
  static values = { url: String, labels: Object };

  look() {
    const digits = this.zipTarget.value.replace(/\D/g, '');
    this.missingTarget.hidden = true;
    // A CEP is Brazil's: eight digits under another nation ask nobody.
    if (this.hasNationTarget && this.nationTarget.value !== 'BR') return;
    if (digits.length !== 8 || digits === this.asked) return;

    this.asked = digits;
    fetch(this.urlValue.replace('ZIP', digits), { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((found) => (found ? this.fill(found) : (this.missingTarget.hidden = false)))
      .catch(() => (this.missingTarget.hidden = false));
  }

  // A line picked in the search beside the CEP (docs/ADDRESS.md § Or search):
  // the same fill, plus the number. A pick nobody resolves changes nothing.
  place({ detail, params }) {
    const url = new URL(params.place, window.location.origin);
    url.searchParams.set('ref', detail.id);
    fetch(url, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((found) => found && this.fill(found))
      .catch(() => {});
  }

  fill(found) {
    if (found.zip) this.zipTarget.value = found.zip;
    if (found.street && !this.streetTarget.value) this.streetTarget.value = found.street;
    if (found.number && !this.numberTarget.value) this.numberTarget.value = found.number;
    if (found.hood && !this.hoodTarget.value) this.hoodTarget.value = found.hood;
    if (found.city_id) {
      this.cityIdTarget.value = found.city_id;
      this.cityTarget.value = found.city;
    }
    this.numberTarget.focus();
    this.dispatch('found', { detail: found });
  }

  // Another nation, another set of cities and other names for the papers
  // (walk/ADDRESS.md screen 6): the city box searches the new one, the old
  // nation's city leaves with it, "CEP" becomes that nation's word, and the
  // document is masked or free as that nation's is.
  nation() {
    const nation = this.nationTarget.value;
    const box = this.cityTarget.closest('[data-controller~="typeahead"]');
    box.dataset.typeaheadParamsValue = JSON.stringify({ nation });
    this.cityTarget.value = '';
    this.cityIdTarget.value = '';

    if (this.hasSearchTarget) {
      const params = JSON.parse(this.searchTarget.dataset.typeaheadParamsValue);
      this.searchTarget.dataset.typeaheadParamsValue = JSON.stringify({ ...params, nation });
    }

    const names = this.labelsValue[nation] || this.labelsValue['*'];
    this.element.querySelector(`label[for="${this.zipTarget.id}"]`).textContent = names.zip;
    const label = this.element.querySelector('label[for$="_doc"]');
    label.textContent = names.doc;

    // The mask is the nation's too: a CPF/CNPJ nation wears doc-input, any
    // other types free. Stimulus connects or lets go as the attribute changes.
    const doc = document.getElementById(label.htmlFor);
    const controllers = new Set((doc.dataset.controller || '').split(/\s+/).filter(Boolean));
    if (names.mask) controllers.add('doc-input');
    else controllers.delete('doc-input');
    doc.dataset.controller = [...controllers].join(' ');
  }
}
